import { Controller } from "@hotwired/stimulus"

// Journées repliables d'un fil (activité récente, notifications) : même geste que les périodes
// de « Récemment consulté » (cf. recent_visits_controller.js). Un intitulé de journée est un
// bouton ([data-action="collapsible-groups#toggle"], clé dans `data-collapsible-groups-group-param`),
// les lignes de la journée vivent dans un corps animable portant la même clé
// (`data-collapsible-groups-target="body"`, `data-group`).
//
// Le contrôleur se pose sur un élément qui survit au rechargement du fil et qui contient aussi
// les frames de pages suivantes : il réapplique l'état à chaque `turbo:frame-load`. Une journée
// coupée entre deux pages (notifications) n'a d'intitulé que sur la première, mais son corps
// porte sa clé sur chacune : tout se replie ensemble.
//
// L'état est mémorisé pour la session de navigation (`data-collapsible-groups-storage-key-value`) :
// changer de filtre ou de période recharge le fil, il ne doit pas rouvrir ce qu'on a replié.
//
// Activité récente seulement (`before-param` / `since-param`) : replier une journée ne doit pas
// laisser le scroll infini en charger la suite. La frame qui la continuait est suspendue, et
// une frame sœur demande directement ce qui précède cette journée. La rouvrir reprend la
// journée là où elle s'était arrêtée, sans recharger les jours déjà affichés en dessous.
export default class extends Controller {
  static targets = ["trigger", "body"]
  static values = { storageKey: String, beforeParam: String, sinceParam: String }

  connect() {
    this.boundFrameLoad = (event) => {
      this.collapseWithin(event.detail?.newFrame || event.target)
      this.apply({ animate: false })
      this.jumpCollapsedContinuations()
    }
    this.boundBeforeRender = (event) => {
      const incoming = event.detail?.newFrame
      this.collapseWithin(incoming)
      // Avant l'insertion : la frame suivante naît déjà suspendue si sa journée est repliée,
      // elle ne lance pas la requête qu'on s'apprête à annuler.
      this.jumpCollapsedContinuations(incoming)
    }
    this.collapsed = this.read()

    this.element.addEventListener("turbo:before-frame-render", this.boundBeforeRender)
    this.element.addEventListener("turbo:frame-load", this.boundFrameLoad)
    this.apply({ animate: false })
    this.jumpCollapsedContinuations()
  }

  disconnect() {
    this.element.removeEventListener("turbo:before-frame-render", this.boundBeforeRender)
    this.element.removeEventListener("turbo:frame-load", this.boundFrameLoad)
  }

  toggle({ params: { group } }) {
    const key = String(group)
    const opening = this.collapsed.has(key)
    opening ? this.collapsed.delete(key) : this.collapsed.add(key)
    this.write()
    this.apply()
    opening ? this.resumeContinuation(key) : this.jumpCollapsedContinuations()
  }

  // La page qui arrive est peinte avec les journées déjà repliées : sinon les vingt lignes
  // s'affichent, puis `turbo:frame-load` les referme — le clignotement.
  collapseWithin(root) {
    if (!root?.querySelectorAll) return

    root.querySelectorAll("[data-group]").forEach((body) => {
      if (!this.collapsed.has(body.dataset.group)) return

      body.classList.add("is-collapsed")
      body.setAttribute("inert", "")
    })
    root.querySelectorAll("[data-collapsible-groups-group-param]").forEach((trigger) => {
      const key = trigger.dataset.collapsibleGroupsGroupParam
      if (this.collapsed.has(key)) trigger.setAttribute("aria-expanded", "false")
    })
  }

  // Une frame dont `previous_day` est une journée repliée en chargerait la suite. On l'arrête
  // et, s'il n'y en a pas déjà une, on demande le jour d'avant.
  jumpCollapsedContinuations(root = this.element) {
    if (!this.hasBeforeParamValue || !root?.querySelectorAll) return

    root.querySelectorAll("turbo-frame[src]").forEach((frame) => {
      if (frame.dataset.skipDay) return

      const day = this.dayParam(frame, "previous_day")
      if (!day || !this.collapsed.has(day)) return

      this.suspendContinuation(frame, day)
      this.ensureSkipFrame(frame, day)
    })
  }

  suspendContinuation(frame, day) {
    if (!frame.dataset.originalSrc) frame.dataset.originalSrc = frame.getAttribute("src")
    frame.dataset.continuationDay = day
    if (frame.hasAttribute("complete")) return

    // Avant de réécrire `src` : un chargement déjà parti serait celui de la journée repliée.
    frame.disabled = true
    if (!this.hasSinceParamValue) return

    const url = this.frameUrl(frame.dataset.originalSrc)
    if (!url || url.searchParams.has(this.sinceParamValue)) return

    url.searchParams.set(this.sinceParamValue, day)
    frame.setAttribute("src", `${url.pathname}${url.search}`)
  }

  ensureSkipFrame(frame, day) {
    const scope = frame.isConnected ? this.element : frame.parentElement
    if (scope?.querySelector(`turbo-frame[data-skip-day="${day}"]`)) return

    const url = this.frameUrl(frame.dataset.originalSrc || frame.getAttribute("src"))
    if (!url) return

    url.searchParams.delete(this.sinceParamValue)
    url.searchParams.set(this.beforeParamValue, day)

    const skip = document.createElement("turbo-frame")
    skip.id = `${frame.id}_before_${day}`
    skip.dataset.skipDay = day
    skip.className = "recent-activity__next-page"
    skip.setAttribute("loading", "lazy")
    skip.setAttribute("src", `${url.pathname}${url.search}`)

    const placeholder = frame.querySelector(".recent-activity__loading")
    if (placeholder) skip.appendChild(placeholder.cloneNode(true))

    frame.insertAdjacentElement("afterend", skip)
  }

  resumeContinuation(day) {
    const filtered = (this.element.dataset.filteredDays || "").split(/\s+/).includes(day)

    this.element.querySelectorAll("turbo-frame[data-continuation-day]").forEach((frame) => {
      if (frame.dataset.continuationDay !== day) return
      // Le filtre de catégorie tient la suite mélangée à l'écart. La rouvrir rechargerait
      // les autres types sous la liste filtrée. La pagination de la catégorie, elle, vit
      // dans son propre fil et peut reprendre.
      if (filtered && !frame.closest(".recent-activity__kind-feed")) return

      frame.disabled = false
      if (frame.hasAttribute("complete")) return

      // Paresseuse et déjà dans la vue : l'observateur a vu l'intersection pendant qu'elle
      // était suspendue, il ne se redéclenche pas. On force une lecture, puis on rend la main.
      frame.setAttribute("loading", "eager")
      frame.setAttribute("loading", "lazy")
    })
  }

  dayParam(frame, name) {
    return this.frameUrl(frame.getAttribute("src"))?.searchParams.get(name) || null
  }

  frameUrl(src) {
    if (!src) return null

    try {
      return new URL(src, window.location.origin)
    } catch {
      return null
    }
  }

  // Animé au clic ; sans animation au chargement, sinon une journée qui arrive déjà repliée
  // s'ouvrirait puis se refermerait sous les yeux.
  apply({ animate = true } = {}) {
    if (!animate) this.element.classList.add("collapsible-groups--instant")

    this.triggerTargets.forEach((trigger) => {
      const key = trigger.dataset.collapsibleGroupsGroupParam
      trigger.setAttribute("aria-expanded", this.collapsed.has(key) ? "false" : "true")
    })
    this.bodyTargets.forEach((body) => {
      const collapsed = this.collapsed.has(body.dataset.group)
      body.classList.toggle("is-collapsed", collapsed)
      // Repliées, les lignes restent dans le DOM le temps de l'animation : hors focus et
      // hors lecteur d'écran.
      body.toggleAttribute("inert", collapsed)
    })

    if (!animate) {
      void this.element.offsetHeight
      this.element.classList.remove("collapsible-groups--instant")
    }
  }

  read() {
    try {
      return new Set(JSON.parse(sessionStorage.getItem(this.storageKeyValue) || "[]"))
    } catch {
      return new Set()
    }
  }

  write() {
    try {
      sessionStorage.setItem(this.storageKeyValue, JSON.stringify([...this.collapsed]))
    } catch {
      // Stockage indisponible (navigation privée) : le repli vaut pour la page en cours.
    }
  }
}
